/** 랜딩 섹션 공통 — 프롬프트 스펙 기준 900px 단일 분기 */
export const LANDING_MOBILE_MQ = '@media (max-width: 900px)';

/**
 * 랜딩 타이포 위계 — viewport에 따라 fluid scale (900px 기준 breakpoint와 병행)
 *
 * display          — 히어로 H1, 스크롤 인트로 타이틀
 * sectionTitle     — FeatureStack 섹션 타이틀
 * sectionTitleSm   — FAQ·Requirements 섹션 타이틀
 * featureCardTitle — FeatureStack 카드 타이틀
 * introBody        — 스크롤 인트로 본문
 * lead             — 히어로 서브타이틀
 * body             — FAQ·Requirements 본문, 플로팅 메타 값
 * bodyMd           — Requirements 서브타이틀, FeatureStack 카드 설명
 * label            — FeatureStack 카드 라벨, FAQ 질문, Requirements 라벨
 * caption          — 말풍선·넛지·플로팅 캡션·플레이스홀더
 * cardName         — 면접관 카드 이름
 * cardQuote        — 면접관 카드 대사
 * micro            — 면접관 카드 태그
 * pill             — FeatureStack pill
 * cta              — CTA 버튼
 */
export const LANDING_FONT = {
  display: 'clamp(38px, 4vw + 20px, 52px)',
  sectionTitle: 'clamp(26px, 2.8vw + 14px, 40px)',
  sectionTitleSm: 'clamp(20px, 1.6vw + 14px, 26px)',
  featureCardTitle: 'clamp(24px, 2.4vw + 14px, 36px)',
  introBody: 'clamp(15px, 1vw + 12px, 18px)',
  lead: 'clamp(15px, 0.6vw + 13px, 17px)',
  body: 'clamp(14px, 0.5vw + 12px, 16px)',
  bodyMd: 'clamp(14px, 0.45vw + 12px, 15.5px)',
  label: 'clamp(13px, 0.45vw + 11px, 15px)',
  caption: 'clamp(12px, 0.35vw + 10px, 14px)',
  cardName: 'clamp(16px, 1.2vw + 11px, 20px)',
  cardQuote: 'clamp(12px, 0.35vw + 10px, 13.5px)',
  micro: 'clamp(10px, 0.25vw + 9px, 11px)',
  pill: 'clamp(11px, 0.3vw + 9px, 12px)',
  cta: 'clamp(14px, 0.5vw + 12px, 16px)',
} as const;

export const LANDING_COLOR = {
  brand: '#00A760',
  brandHover: '#00C471',
  brandActive: '#00915A',
  ink: '#111315',
  dark: '#0E1013',
  bodyMuted: 'rgba(17,19,21,.6)',
  caption: 'rgba(17,19,21,.4)',
  whiteMuted: 'rgba(255,255,255,.55)',
  whiteCaption: 'rgba(255,255,255,.42)',
} as const;

export const LANDING_EASE = '180ms cubic-bezier(.2,0,.2,1)';

/**
 * 랜딩 간격·패딩 위계 — clamp 기반 fluid scale
 *
 * pageX            — 페이지 좌우 인셋
 * sectionTop       — 섹션 상단
 * sectionBottom    — 섹션 하단 (일반)
 * sectionBottomLg  — FAQ 하단 (플로팅 CTA 여유)
 * sectionGap       — 섹션 내부 주요 stack gap
 * cardListGap      — FeatureStack 카드 간격
 * stackGap         — 카드 copy·폼 stack gap
 * rowGap           — FAQ·Requirements 2열 grid gap
 * rowY             — FAQ·Requirements 행 세로 padding
 * titleBottom      — 섹션 타이틀 하단
 * subtitleBottom   — Requirements 서브타이틀 하단
 * heroTop / heroX  — 히어로 섹션 패딩
 * introStack       — 인트로·스테이지 상단 margin
 * leadStack        — 히어로 서브타이틀 상단
 * featureCard*     — FeatureStack 카드 내부 패딩
 * cardRadius       — FeatureStack 카드 radius
 * cardCaptionPad   — 면접관 카드 캡션
 * bubblePad        — 말풍선 padding
 * chipPad          — 넛지·pill padding
 * ctaPadX          — CTA 버튼 좌우
 */
export const LANDING_SPACE = {
  pageX: 'clamp(20px, 4vw, 40px)',
  sectionTop: 'clamp(48px, 5vw + 24px, 64px)',
  sectionBottom: 'clamp(48px, 6vw + 24px, 72px)',
  sectionBottomLg: 'clamp(96px, 12vw + 40px, 140px)',
  sectionGap: 'clamp(24px, 2vw + 16px, 32px)',
  cardListGap: 'clamp(16px, 1.5vw + 10px, 20px)',
  stackGap: 'clamp(12px, 1vw + 8px, 16px)',
  rowGap: 'clamp(8px, 2vw + 4px, 24px)',
  rowY: 'clamp(16px, 2vw + 8px, 20px)',
  titleBottom: 'clamp(20px, 2vw + 12px, 28px)',
  subtitleBottom: 'clamp(16px, 2vw + 8px, 24px)',
  heroTop: 'clamp(56px, 6vw + 24px, 72px)',
  heroX: 'clamp(20px, 2vw + 12px, 24px)',
  introStack: 'clamp(16px, 2vw + 8px, 24px)',
  leadStack: 'clamp(12px, 1.5vw + 6px, 18px)',
  featureCardPadTop: 'clamp(28px, 4vw + 16px, 52px)',
  featureCardPadLeft: 'clamp(20px, 4vw + 8px, 52px)',
  featureCardPadBottom: 'clamp(28px, 4vw + 16px, 52px)',
  cardRadius: 'clamp(24px, 3vw + 12px, 32px)',
  cardCaptionPad: 'clamp(14px, 1.5vw + 8px, 18px)',
  bubblePadY: 'clamp(10px, 1vw + 6px, 12px)',
  bubblePadX: 'clamp(12px, 1vw + 8px, 14px)',
  chipPadY: 'clamp(9px, 0.8vw + 6px, 11px)',
  chipPadX: 'clamp(12px, 1.5vw + 8px, 16px)',
  ctaPadX: 'clamp(20px, 3vw + 8px, 28px)',
  modalPad: 'clamp(16px, 3vw, 20px)',
  screenHeight: 'clamp(260px, 40vw, 400px)',
  badgeInset: 'clamp(20px, 4vw + 8px, 44px)',
} as const;
